"use client";

import React, { useState } from "react";
import NextImage from "next/image";
import Icon from "@/components/ui/Icon";
import { BlogMediaData } from "@/data/blogPosts";

interface BlogImageRendererProps {
  media: BlogMediaData;
}

export default function BlogImageRenderer({ media }: BlogImageRendererProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  if (!media || !media.url) return null;

  return (
    <figure className="my-10 rounded-2xl overflow-hidden border border-outline-variant/40 bg-surface-container-lowest shadow-lg">
      {/* Explicit Aspect-Ratio and Min-Height Container */}
      <div className="relative w-full aspect-[16/10] sm:aspect-[16/9] min-h-[240px] sm:min-h-[380px] bg-neutral-900 overflow-hidden">
        {/* Placeholder / Skeleton */}
        {!isLoaded && !hasError && (
          <div className="absolute inset-0 bg-neutral-900 animate-pulse flex items-center justify-center z-0">
            <div className="flex flex-col items-center gap-2 text-neutral-500">
              <Icon name="monitor" size={32} className="animate-pulse opacity-40" />
              <span className="text-xs font-mono tracking-wider opacity-60">LOADING ARCHITECTURE DIAGRAM...</span>
            </div>
          </div>
        )}

        {/* The Image */}
        <NextImage
          src={media.url}
          alt={media.alt || media.caption || "Technical Architecture Diagram"}
          fill
          sizes="(max-width: 768px) 100vw, 840px"
          priority={false}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          onLoad={() => setIsLoaded(true)}
          onError={() => {
            setHasError(true);
            setIsLoaded(true);
          }}
          unoptimized={media.url.endsWith(".svg")}
        />

        {/* Error Fallback */}
        {hasError && (
          <div className="absolute inset-0 bg-neutral-900 flex flex-col items-center justify-center p-6 text-neutral-400 gap-2">
            <Icon name="shield_alert" size={32} className="text-primary/70" />
            <span className="text-sm font-semibold text-neutral-300">Architecture Diagram</span>
            <span className="text-xs text-neutral-500 font-mono">{media.url}</span>
          </div>
        )}
      </div>

      {media.caption && (
        <figcaption className="px-5 py-3.5 text-xs sm:text-sm text-secondary bg-surface-container-low/80 border-t border-outline-variant/25 flex items-center gap-2.5">
          <Icon name="monitor" size={16} className="text-primary flex-shrink-0" />
          <span className="font-medium text-on-surface-variant leading-relaxed">{media.caption}</span>
        </figcaption>
      )}
    </figure>
  );
}
